import React, { useEffect, useState } from 'react';
import { getWasteData, getWasteDataExcel } from '../services/wasteService';
import { ArrowDownIcon, SearchIcon } from '../assets/icons';
import WasteDetailModal from '../components/WasteModals/WasteDetailModal';
import { formatDate, formatDescription, formatNumber, getLatestReceivedRecordWithDescription } from '../helpers/stringHelpers';
import Pagination from '../components/Pagination';

const TIME_PERIODS = [
  { label: 'Tümü', value: '' },
  { label: 'Bugün', value: 0 },
  { label: '1 Hafta', value: 7 },
  { label: '1 Ay', value: 30 },
  { label: '1 Yıl', value: 365 }
];

const Waste = () => {
  const [wasteData, setWasteData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDays, setSelectedDays] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedWaste, setSelectedWaste] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statistics, setStatistics] = useState({
    totalBag: 0,
    totalGivedPlaceForBag: 0,
    totalReceivedPlaceForBag: 0,
    totalPlace: 0,
    totalReceivedPlace: 0,
    totalGivedPlace: 0
  });

  useEffect(() => {
    fetchWasteData();
  }, [currentPage, selectedDays]);

  const fetchWasteData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getWasteData({
        offset: currentPage,
        days: selectedDays,
        search: searchTerm
      });

      if (response.status === 200 && response.data) {
        setWasteData(response.data);
        setTotalPages(Math.ceil(response.totalRecords / 15));
        setStatistics(response.statistic);
      } else {
        setWasteData([]);
        setTotalPages(0);
        setStatistics({
          totalBag: 0,
          totalGivedPlaceForBag: 0,
          totalReceivedPlaceForBag: 0,
          totalPlace: 0,
          totalReceivedPlace: 0,
          totalGivedPlace: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch waste data:', error);
      setError('Atık verileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handlePeriodChange = (e) => {
    setSelectedDays(e.target.value ? Number(e.target.value) : '');
    setCurrentPage(0);
  };

  const handleRowClick = (waste) => {
    setSelectedWaste(waste);
    setIsModalOpen(true);
  };

  const handleExcelDownload = async () => {
    try {
      await getWasteDataExcel(selectedDays);
    } catch (error) {
      console.error('Failed to download excel:', error);
      // You might want to show an error message to the user here
    }
  };

  if (isLoading) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Atık Verileri</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Atık Verileri</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="waste-container">
      <h1 className="page-title">Atık Verileri</h1>

      {/* Search and Filter Section */}
      <div className="search-filter-section">

        <div className="filter-container">
          <button className="download-excel-button" onClick={handleExcelDownload}>
            <p>Excel Çıktısı Al</p>
            <ArrowDownIcon color='white' className="filter-icon" />
          </button>
          <select value={selectedDays} onChange={handlePeriodChange}>
            {TIME_PERIODS.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="statistics-section">
        <div className="stat-card-container">
          <div className="stat-card">
            <h3>Toplam<br />Atık/Poşet Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalBag)}</p>
            <p className='stat-card-text'>adet</p>
          </div>
          <div className="stat-card green">
            <h3>Toplam Hanelere<br />Verilen Poşet Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalGivedPlaceForBag)}</p>
            <p className='stat-card-text'>adet poşet</p>
          </div>
          <div className="stat-card">
            <h3>Toplam Hanelerden<br />Alınan Atık Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalReceivedPlaceForBag)}</p>
            <p className='stat-card-text'>adet poşet</p>
          </div>
        </div>
        <div className="stat-card-container">
          <div className="stat-card green">
            <h3>Toplam<br />Hane Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalPlace)}</p>
            <p className='stat-card-text'>hane</p>
          </div>
          <div className="stat-card">
            <h3>Poşet Verilen<br />Hane Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalGivedPlace)}</p>
            <p className='stat-card-text'>adet poşet</p>
          </div>
          <div className="stat-card green">
            <h3>Poşet Alınan<br />Hane Sayısı</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalReceivedPlace)}</p>
            <p className='stat-card-text'>adet poşet</p>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-container">
        <table className="waste-table">
          <thead>
            <tr>
              {/* <th>Adres</th> */}
              <th>İlçe</th>
              <th>Mahalle</th>
              <th>Sokak</th>
              <th>Apartman Adı<br /><span>Daire No</span></th>
              <th>
                <p>Verilen Poşet Sayısı</p>
                <span>Tarih</span>
              </th>
              <th>
                <p>Alınan Atık Sayısı</p>
                <span>Tarih</span>
              </th>
              <th>Tanımlanan QR'lar</th>
              {/* <th>Gidiş Sayısı</th> */}
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {wasteData.length > 0 ? (
              wasteData.map((record) => {
                const latestReceivedWithDesc = getLatestReceivedRecordWithDescription(record.records);

                return (
                  <tr
                    key={record.qr}
                    onClick={() => handleRowClick(record)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* <td className='table-address'>{record.address}</td> */}
                    <td className='table-address'><span className='district'>{record.records[0].district}</span></td>
                    <td className='table-address'>{record.records[0].mahalle}</td>
                    <td className='table-address'>{record.records[0].sokak}</td>
                    <td className='table-address'>{record.records[0].apartman}<br /><span className='table-address-number'>{record.records[0].daire}</span></td>
                    <td>
                      <span className='table-bag'>{record.records.length - record.records.filter(r => r.dateReceived).length} poşet</span>
                      <span className='table-date'>
                        <br />{formatDate(record.records.reduce((latest, r) =>
                          !latest || (r.dateGiven && r.dateGiven > latest) ? r.dateGiven : latest,
                          null
                        ))}
                      </span>
                    </td>
                    <td>
                      <span className='table-bag'>{record.records.filter(r => r.dateReceived).length} poşet</span>
                      <span className='table-date'>
                        <br />{formatDate(record.records.reduce((latest, r) =>
                          !latest || (r.dateReceived && r.dateReceived > latest) ? r.dateReceived : latest,
                          null
                        ))}
                      </span>
                    </td>
                    <td className='table-qr'>{record.records.length} QR Kaydı</td>
                    {/* <td className='table-badge-cell'>
                      <span className='table-badge'>
                        {new Set(record.records.map(r => r.dateGiven?.split('T')[0])).size}.Gidiş
                      </span>
                    </td> */}
                    <td>
                      <p className='description'>
                        {latestReceivedWithDesc ? formatDescription(latestReceivedWithDesc.description) : ''}
                      </p>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  Veri bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <WasteDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        wasteData={selectedWaste}
      />
    </div>
  );
};

export default Waste;
